// DealerInviteCard.tsx
// Shows on the Dealer Passport for pending invites
// Handles accept / decline / standby with call-by time

import React, { FC, useState } from 'react';
import { db } from '../../firebase';
import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { Box, Flex, Text, Button } from '@chakra-ui/react';
import NotificationService from '../../services/notifications/NotificationService';

const notifService = new NotificationService();

interface DealerInviteCardProps {
  invite: {
    eventId: string;
    eventName: string;
    eventStart: string;
    tdUid: string;
    tdName: string;
    deadline: string;
    urgent: boolean;
    dealerUid: string;
  };
  onResponded: (eventId: string) => void;
}

const DealerInviteCard: FC<DealerInviteCardProps> = ({ invite, onResponded }) => {
  const [responding, setResponding] = useState(false);
  const [showStandbyPicker, setShowStandbyPicker] = useState(false);
  const [standbyCallByTime, setStandbyCallByTime] = useState('');

  const deadlineDate = new Date(invite.deadline);
  const now = Date.now();
  const msLeft = deadlineDate.getTime() - now;
  const hoursLeft = Math.max(0, Math.floor(msLeft / 3600000));
  const minutesLeft = Math.max(0, Math.floor((msLeft % 3600000) / 60000));
  const isExpired = msLeft <= 0;
  const isUrgent = invite.urgent || hoursLeft < 6;

  const eventDate = new Date(invite.eventStart).toLocaleDateString('en-CA', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  const respond = async (status: 'accepted' | 'declined' | 'standby') => {
    if (responding) return;
    if (status === 'standby' && !standbyCallByTime) {
      setShowStandbyPicker(true);
      return;
    }
    setResponding(true);
    try {
      // Update event dealer status
      const update: any = {
        dealer_status: status,
        dealer_responded_at: serverTimestamp(),
      };
      if (status === 'standby' && standbyCallByTime) {
        update.dealer_standby_call_by_time = standbyCallByTime;
      }
      await updateDoc(doc(db, 'events', invite.eventId), update);

      // Update dealer reliability metrics
      const dealerRef = doc(db, 'dealers', invite.dealerUid);
      const dealerSnap = await getDoc(dealerRef);
      if (dealerSnap.exists()) {
        const d = dealerSnap.data();
        const updates: any = {
          response_count: (d.response_count ?? 0) + 1,
          updated_at: serverTimestamp(),
        };
        if (status === 'accepted') updates.acceptance_count = (d.acceptance_count ?? 0) + 1;
        if (status === 'standby') updates.standby_count = (d.standby_count ?? 0) + 1;
        // Recompute response rate
        const newResponseCount = updates.response_count;
        const totalInvites = d.invite_count ?? 1;
        updates.response_rate = Math.round((newResponseCount / totalInvites) * 100);
        await updateDoc(dealerRef, updates);
      }

      // Notify TD
      await notifService.notifyTDDealerResponded({
        tdUid: invite.tdUid,
        dealerName: 'Your dealer',
        eventId: invite.eventId,
        eventName: invite.eventName,
        status,
        standbyCallByTime: standbyCallByTime || undefined,
      });

      onResponded(invite.eventId);
    } catch (e) { console.error(e); }
    setResponding(false);
  };

  const C = {
    card: '#131720', border: '#1E2330', gold: '#D4A017',
    teal: '#19E6CF', white: '#F0F4FF', ink: '#C8D0E0', dim: '#6B7590',
    green: '#22C55E', red: '#EF4444', amber: '#F59E0B',
  };
  const F = { ui: "'Rajdhani','Arial',sans-serif", mono: "'Share Tech Mono',monospace" };

  const urgentBorder = isUrgent ? C.red : isExpired ? C.dim : C.amber;
  const urgentBg = isUrgent ? '#2D0D0D' : isExpired ? '#111' : '#1A1200';

  return (
    <Box border={`2px solid ${urgentBorder}`} borderRadius={8} overflow="hidden" mb={3}>
      {/* Header */}
      <Flex bg={urgentBg} px={4} py={2} align="center" justify="space-between">
        <Text style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.2em',
          color: isExpired ? C.dim : isUrgent ? '#FCA5A5' : C.amber, textTransform: 'uppercase' }}>
          {isExpired ? '⏰ EXPIRED — NO RESPONSE RECORDED' : isUrgent ? '⚡ URGENT INVITE' : '🟡 DEALER INVITE'}
        </Text>
        {!isExpired && (
          <Text style={{ fontFamily: F.mono, fontSize: 11, color: isUrgent ? '#FCA5A5' : C.amber }}>
            {hoursLeft > 0 ? `${hoursLeft}h ${minutesLeft}m left` : `${minutesLeft}m left`}
          </Text>
        )}
      </Flex>

      {/* Body */}
      <Box bg={C.card} px={4} py={3}>
        <Text style={{ fontFamily: F.ui, fontSize: 16, fontWeight: 700, color: C.white, marginBottom: 2 }}>
          {invite.eventName}
        </Text>
        <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.dim, marginBottom: 4 }}>
          {eventDate}
        </Text>
        <Text style={{ fontFamily: F.ui, fontSize: 13, color: C.ink, marginBottom: 12 }}>
          Invited by <b style={{ color: C.white }}>{invite.tdName}</b>
          {' · '}Response needed by {deadlineDate.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </Text>

        {!isExpired && (
          <>
            {/* Standby call-by picker */}
            {showStandbyPicker && (
              <Box mb={3} p={3} bg="#1A1200" border={`1px solid ${C.amber}`} borderRadius={6}>
                <Text style={{ fontFamily: F.ui, fontSize: 12, color: C.amber, marginBottom: 8, letterSpacing: '0.1em' }}>
                  STANDBY: What is the latest time the TD can activate you?
                </Text>
                <input
                  type="datetime-local"
                  value={standbyCallByTime}
                  onChange={e => setStandbyCallByTime(e.target.value)}
                  style={{ background: '#111', border: `1px solid ${C.border}`, borderRadius: 4,
                    padding: '6px 10px', color: C.white, fontSize: 13, fontFamily: F.ui,
                    width: '100%', marginBottom: 8 }}
                />
                <Flex gap={2}>
                  <Box as="button" px={3} py={2} bg="#0A1A10" border={`1px solid ${C.green}`}
                    borderRadius={4} fontSize="12px" fontWeight="700" color={C.green}
                    fontFamily={F.ui} cursor={!standbyCallByTime ? 'not-allowed' : 'pointer'}
                    opacity={!standbyCallByTime ? 0.5 : 1}
                    onClick={() => standbyCallByTime && respond('standby')}>
                    Confirm Standby
                  </Box>
                  <Box as="button" px={3} py={2} bg={C.card} border={`1px solid ${C.border}`}
                    borderRadius={4} fontSize="12px" fontWeight="700" color={C.dim}
                    fontFamily={F.ui} cursor="pointer"
                    onClick={() => setShowStandbyPicker(false)}>
                    Cancel
                  </Box>
                </Flex>
              </Box>
            )}

            {/* Response buttons */}
            {!showStandbyPicker && (
              <Flex gap={2} flexWrap="wrap">
                <Box as="button" px={4} py={2} bg="#052E16" border={`1px solid ${C.green}`}
                  borderRadius={6} fontSize="13px" fontWeight="700" color="#86EFAC"
                  fontFamily={F.ui} cursor={responding ? 'wait' : 'pointer'}
                  opacity={responding ? 0.7 : 1}
                  onClick={() => respond('accepted')}>
                  ✓ Accept
                </Box>
                <Box as="button" px={4} py={2} bg="#1A1200" border={`1px solid ${C.amber}`}
                  borderRadius={6} fontSize="13px" fontWeight="700" color={C.amber}
                  fontFamily={F.ui} cursor={responding ? 'wait' : 'pointer'}
                  opacity={responding ? 0.7 : 1}
                  onClick={() => setShowStandbyPicker(true)}>
                  🟡 Standby if needed
                </Box>
                <Box as="button" px={4} py={2} bg="#2D0D0D" border={`1px solid ${C.red}`}
                  borderRadius={6} fontSize="13px" fontWeight="700" color="#FCA5A5"
                  fontFamily={F.ui} cursor={responding ? 'wait' : 'pointer'}
                  opacity={responding ? 0.7 : 1}
                  onClick={() => respond('declined')}>
                  ✕ Decline
                </Box>
              </Flex>
            )}
          </>
        )}

        {isExpired && (
          <Text style={{ fontFamily: F.ui, fontSize: 12, color: C.dim, fontStyle: 'italic' }}>
            This invite expired without a response. This has been noted on your reliability record.
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default DealerInviteCard;
